import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { Button, Flex, Icon, Modal, Textinput, Typo, theme } from '../../../ui';
import SearchSelect from '../../../ui/SearchSelect';
import { useReaders } from '../../users/hooks/useUsersState';
import { useClassroomActions } from '../hooks';

const STEPS = { NAME: 0, SUBJECTS: 1, STUDENTS: 2, REVIEW: 3 };
const TOTAL_STEPS = 4;

const StepContainer = styled.div`
  min-height: 300px;
  padding: 8px 0;
`;

const ProgressDots = styled(Flex)`
  padding: 16px 0 24px;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  transition: background 0.2s ease;
`;

const ItemRow = styled(Flex)`
  padding: 10px 12px;
  background: ${theme.colors.NEU_50};
  border-radius: 8px;
`;

const ItemList = styled(Flex)`
  max-height: 260px;
  overflow-y: auto;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
`;

const ReviewCard = styled.div`
  background: ${theme.colors.NEU_50};
  border-radius: 8px;
  padding: 16px;
`;

const Footer = styled(Flex)`
  padding-top: 16px;
  border-top: 1px solid ${theme.colors.NEU_100};
`;

const ClassroomWizard = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createClassroom } = useClassroomActions();

  const [step, setStep] = useState(STEPS.NAME);
  const [classroomName, setClassroomName] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // ─── User search (same pattern as SendInvite.js) ─────────────────────────

  const isStudentsStep = step === STEPS.STUDENTS;
  const hasSearchText = Boolean(searchText && searchText.trim().length >= 2);

  const {
    readers: users = [],
    isLoading,
    isFetching,
  } = useReaders(
    {
      pageLength: 50,
      ...(hasSearchText && { searchText: searchText.trim() }),
      sortBy: 'name',
    },
    { skip: !isStudentsStep }
  );

  const isLoadingUsers = isLoading || isFetching;

  const debouncedSearch = useMemo(() => debounce((value) => {
    setSearchText(value);
  }, 500), []);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSearchChange = useCallback((value) => {
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleStudentsChange = useCallback((newStudents) => {
    setSelectedStudents(newStudents);
    setSearchText('');
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  // ─── Subject handlers ───────────────────────────────────────────────────────

  const addSubject = useCallback(() => {
    const trimmed = newSubject.trim();
    if (trimmed && !subjects.includes(trimmed)) {
      setSubjects((prev) => [...prev, trimmed]);
      setNewSubject('');
    }
  }, [newSubject, subjects]);

  const removeSubject = useCallback((index) => {
    setSubjects((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubjectKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addSubject();
      }
    },
    [addSubject]
  );

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const canGoNext = () => {
    if (step === STEPS.NAME) return classroomName.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (step < STEPS.REVIEW) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // ─── Create ─────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const result = await createClassroom({
        name: classroomName.trim(),
        subjects,
        selectedStudents,
      });
      if (result?.groupId) {
        onClose();
        navigate(`/classroom/${result.groupId}`);
      }
    } catch (err) {
      setError(err?.message || t('classroom.wizardError', 'Failed to create classroom. Please try again.'));
      setIsCreating(false);
    }
  };

  // ─── Helpers for review ─────────────────────────────────────────────────────

  const getStudentDisplayName = (student) => {
    if (student.name) return student.name;
    if (student.email) return student.email;
    if (student.inputValue) return student.inputValue;
    if (typeof student === 'string') return student;
    return '?';
  };

  // ─── Render steps ───────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {
      case STEPS.NAME:
        return (
          <StepContainer>
            <Flex direction='column' gap='16'>
              <Typo.ContentSmallHeader>
                {t('classroom.wizardNameTitle', 'Name your classroom')}
              </Typo.ContentSmallHeader>
              <Typo.MediumText color={theme.colors.NEU_600}>
                {t(
                  'classroom.wizardNameDescription',
                  'Choose a name for your classroom. This will be visible to all students and teachers.'
                )}
              </Typo.MediumText>
              <Textinput
                label={t('classroom.classroomName', 'Classroom name')}
                placeholder={t('classroom.classroomNamePlaceholder', 'e.g. Grade 9A - Spring 2025')}
                value={classroomName}
                onChange={(e) => setClassroomName(e.target.value)}
                autoFocus
              />
            </Flex>
          </StepContainer>
        );

      case STEPS.SUBJECTS:
        return (
          <StepContainer>
            <Flex direction='column' gap='16'>
              <Typo.ContentSmallHeader>
                {t('classroom.wizardSubjectsTitle', 'Add units')}
              </Typo.ContentSmallHeader>
              <Typo.MediumText color={theme.colors.NEU_600}>
                {t(
                  'classroom.wizardSubjectsDescription',
                  'Add the units for this classroom. Each unit gets its own live lessons, discussions, and assignments. You can skip this and add them later.'
                )}
              </Typo.MediumText>
              <Flex gap='8' align='flex-end'>
                <div style={{ flex: 1 }}>
                  <Textinput
                    label={t('classroom.subjectName', 'Unit name')}
                    placeholder={t('classroom.subjectPlaceholder', 'e.g. Mathematics')}
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    onKeyDown={handleSubjectKeyDown}
                  />
                </div>
                <Button onClick={addSubject} disabled={!newSubject.trim()}>
                  {t('globals.add', 'Add')}
                </Button>
              </Flex>
              {subjects.length > 0 && (
                <ItemList direction='column' gap='6'>
                  {subjects.map((subject, index) => (
                    <ItemRow key={index} align='center' justify='space-between'>
                      <Flex align='center' gap='8'>
                        <Icon.Course color={theme.colors.PRIMARY} size='x-small' />
                        <Typo.MediumText>{subject}</Typo.MediumText>
                      </Flex>
                      <RemoveButton onClick={() => removeSubject(index)}>
                        <Icon.Close color={theme.colors.NEU_300} size='x-small' />
                      </RemoveButton>
                    </ItemRow>
                  ))}
                </ItemList>
              )}
            </Flex>
          </StepContainer>
        );

      case STEPS.STUDENTS:
        return (
          <StepContainer>
            <Flex direction='column' gap='16'>
              <Typo.ContentSmallHeader>
                {t('classroom.wizardStudentsTitle', 'Add students')}
              </Typo.ContentSmallHeader>
              <Typo.MediumText color={theme.colors.NEU_600}>
                {t(
                  'classroom.wizardStudentsDescription',
                  'Search for existing users or type an email to invite new students. You can skip this and add students later.'
                )}
              </Typo.MediumText>
              <SearchSelect
                creatable
                optionType='email'
                multiple
                setValue={handleStudentsChange}
                selectedOptions={selectedStudents}
                id='classroom-students'
                options={users}
                label={t('classroom.searchStudents', 'Search by name or type an email...')}
                onSearchChange={handleSearchChange}
                loading={isLoadingUsers}
                disableLocalFilter
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option}>
                    <div>
                      <Typo.Text>{option.name}</Typo.Text>
                      <Typo.Info>{option.email}</Typo.Info>
                    </div>
                  </li>
                )}
              />
              {selectedStudents.length > 0 && (
                <Typo.SmallText color={theme.colors.NEU_600}>
                  {selectedStudents.length} {t('classroom.studentsAdded', 'student(s) added')}
                </Typo.SmallText>
              )}
            </Flex>
          </StepContainer>
        );

      // Review step
      default:
        return (
          <StepContainer>
            <Flex direction='column' gap='16'>
              <Typo.ContentSmallHeader>
                {t('classroom.wizardReviewTitle', 'Review and create')}
              </Typo.ContentSmallHeader>
              <Typo.MediumText color={theme.colors.NEU_600}>
                {t('classroom.wizardReviewDescription', 'Review your classroom setup before creating it.')}
              </Typo.MediumText>
              <ReviewCard>
                <Flex direction='column' gap='12'>
                  <Flex direction='column' gap='4'>
                    <Typo.SmallText color={theme.colors.NEU_300}>
                      {t('classroom.classroomName', 'Classroom name')}
                    </Typo.SmallText>
                    <Typo.MediumText style={{ fontWeight: 600 }}>{classroomName}</Typo.MediumText>
                  </Flex>
                  <Flex direction='column' gap='4'>
                    <Typo.SmallText color={theme.colors.NEU_300}>
                      {t('classroom.units', 'Units')}
                    </Typo.SmallText>
                    {subjects.length > 0 ? (
                      <Flex direction='column' gap='4'>
                        {subjects.map((subject, index) => (
                          <Flex key={index} align='center' gap='6'>
                            <Icon.Course color={theme.colors.PRIMARY} size='x-small' />
                            <Typo.MediumText>{subject}</Typo.MediumText>
                          </Flex>
                        ))}
                      </Flex>
                    ) : (
                      <Typo.MediumText color={theme.colors.NEU_300}>
                        {t('classroom.noSubjectsAdded', 'No units added — you can add them later')}
                      </Typo.MediumText>
                    )}
                  </Flex>
                  <Flex direction='column' gap='4'>
                    <Typo.SmallText color={theme.colors.NEU_300}>
                      {t('classroom.students', 'Students')}
                    </Typo.SmallText>
                    {selectedStudents.length > 0 ? (
                      <Flex direction='column' gap='4'>
                        {selectedStudents.map((student, index) => (
                          <Flex key={index} align='center' gap='6'>
                            <Icon.Profile color={theme.colors.PRIMARY} size='x-small' />
                            <Typo.MediumText>{getStudentDisplayName(student)}</Typo.MediumText>
                          </Flex>
                        ))}
                      </Flex>
                    ) : (
                      <Typo.MediumText color={theme.colors.NEU_300}>
                        {t('classroom.noStudentsAdded', 'No students added — you can add them later')}
                      </Typo.MediumText>
                    )}
                  </Flex>
                </Flex>
              </ReviewCard>
              {error && (
                <Typo.SmallText color={theme.colors.ERROR_RED}>{error}</Typo.SmallText>
              )}
            </Flex>
          </StepContainer>
        );
    }
  };

  const isReviewStep = step === STEPS.REVIEW;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('classroom.createClassroom', 'Create Classroom')}
      width='560px'
    >
      <ProgressDots gap='6' justify='center'>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <Dot key={i} $active={i <= step} />
        ))}
      </ProgressDots>

      {renderStep()}

      <Footer justify='space-between' align='center'>
        <div>
          {step > 0 && (
            <Button variant='text' onClick={handleBack} disabled={isCreating}>
              {t('globals.back', 'Back')}
            </Button>
          )}
        </div>
        <Flex gap='8'>
          <Button variant='text' onClick={onClose} disabled={isCreating}>
            {t('globals.cancel', 'Cancel')}
          </Button>
          {isReviewStep ? (
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating
                ? t('classroom.creating', 'Creating...')
                : t('classroom.createClassroom', 'Create Classroom')}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={!canGoNext()}>
              {t('globals.next', 'Next')}
            </Button>
          )}
        </Flex>
      </Footer>
    </Modal>
  );
};

export default ClassroomWizard;
